import { getCookie } from 'widgets/toolbox/cookie';

const RECENTLY_VIEWED_ENTRY = 'recentlyViewed';
const RECENTLY_VIEWED_ITEMS_LIMIT = 12;
const SERVICE_CHANGE_EVENT = 'consentServiceChange';
const SERVICE_PRODUCT_TRACKING = 'Salesforce Products Tracking';

/**
 * @description Extended RecentlyViewedMixin implementation
 * @param RecentlyViewedMixin widget for extending
 * @returns Recently Viewed Mixin
 */
function CoreRecentlyViewedMixinClassCreator(RecentlyViewedMixin: import('widgets/product/RecentlyViewedMixin').TRecentlyViewedMixin) {
    /**
     * @category widgets
     * @subcategory product
     * @class CoreRecentlyViewedMixin
     * @augments RecentlyViewedMixin
     * @classdesc Represents RecentlyViewed functionality.
     * Uses only if Einstein recently viewed functionality for some reason cannot be used. Implement next features:
     * 1. Checks if products tracking allowed
     * 2. Stores product IDs (based on visited pages) in the cookie `recentlyViewed` if tracking allowed
     * @property {boolean} data-recently-viewed-tracking-allowed - determines if recently view tracking allowed based on preference
     * @property {string} data-master-pid - master product ID
     */
    class CoreRecentlyViewedMixin extends RecentlyViewedMixin {
        isConsentGiven: boolean = false;

        isInstanceInitialized: boolean = false;

        prefs() {
            return {
                ...super.prefs()
            };
        }

        init() {
            this.eventBus().on(SERVICE_CHANGE_EVENT, 'processServiceChange');
            this.eventBus().emit('consentServiceRequest', SERVICE_PRODUCT_TRACKING);

            super.init();
        }

        /**
         * @description Handles changes to service consent.
         * @param type - The type of service for which the consent change is being processed.
         * @param accepted - A boolean indicating whether the consent was accepted or declined.
         */
        processServiceChange(type: string, accepted: boolean) {
            if (type === SERVICE_PRODUCT_TRACKING) {
                this.isConsentGiven = accepted;
                this.trackViewedItem(this.prefs().pid?.toString());
            }
        }

        /**
         * @description Saves visited product to cookies
         * @param pid - visited master product ID
         */
        trackViewedItem(pid = '') {
            if (!pid || !this.isConsentGiven || this.isInstanceInitialized || !this.isTrackingAllowed()) {
                return;
            }

            let trackedProducts = this.getViewedItems(getCookie(RECENTLY_VIEWED_ENTRY));

            trackedProducts = trackedProducts.filter(viewedItem => viewedItem !== pid);

            trackedProducts.unshift(pid);

            if (trackedProducts.length > RECENTLY_VIEWED_ITEMS_LIMIT) {
                trackedProducts = trackedProducts.slice(1, RECENTLY_VIEWED_ITEMS_LIMIT);
            }

            this.saveViewedItems(trackedProducts);
            this.isInstanceInitialized = true;
        }
    }

    return CoreRecentlyViewedMixin;
}

export type TCoreRecentlyViewedMixin = ReturnType<typeof CoreRecentlyViewedMixinClassCreator>;

export type TCoreRecentlyViewedMixinInstance = InstanceType<TCoreRecentlyViewedMixin>;

export default CoreRecentlyViewedMixinClassCreator;
